import { createFileRoute, useNavigate, useParams, useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { getBillDetail } from '@/lib/sales-api';
import { useSalesFilters } from '@/lib/sales-filters';
import { fmtINR, fmtNumber, fmtDate } from '@/lib/format';

export const Route = createFileRoute('/_app/sales/bill/$storeCode/$billDate/$billNo')({ component: Page });

function Page() {
  const { storeCode, billDate, billNo } = useParams({ from: '/_app/sales/bill/$storeCode/$billDate/$billNo' });
  const { store } = useSalesFilters();
  const nav = useNavigate();
  const router = useRouter();

  const q = useQuery({
    queryKey: ['sales:bill', store, storeCode, billDate, billNo],
    queryFn: () => getBillDetail({ storeCode, billDate, billNo, store }),
    staleTime: 5 * 60_000,
  });

  const goBack = () => {
    if (router.history.length > 1) router.history.back();
    else nav({ to: '/sales/bills' });
  };

  return (
    <div className="space-y-4">
      <div>
        <Button variant="ghost" size="sm" onClick={goBack} className="-ml-2">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to bills
        </Button>
      </div>

      {q.isLoading && <p>Loading…</p>}

      {q.error && (
        <Card>
          <CardContent className="space-y-2 p-6">
            <p className="text-sm text-destructive">
              {(q.error as Error).message === 'NOT_FOUND' && 'This bill no longer exists.'}
              {(q.error as Error).message === 'FORBIDDEN' && "You don't have access to this bill."}
              {(q.error as Error).message !== 'NOT_FOUND' && (q.error as Error).message !== 'FORBIDDEN' &&
                `Couldn't load bill: ${(q.error as Error).message}`}
            </p>
            <Button variant="outline" size="sm" onClick={goBack}>← Back to bills</Button>
          </CardContent>
        </Card>
      )}

      {q.data && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {q.data.kind === 'return' ? 'Return' : 'Bill'} {q.data.header.billNo}
                {q.data.kind === 'return' && (
                  <span className="rounded bg-rose-soft px-1.5 py-0.5 text-xs font-medium text-rose-deep">RETURN</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                <div><dt className="text-muted-foreground">Date</dt><dd>{fmtDate(q.data.header.billDate)}</dd></div>
                <div><dt className="text-muted-foreground">Store</dt><dd>{q.data.header.storeName ?? q.data.header.storeCode}</dd></div>
                <div><dt className="text-muted-foreground">Customer</dt><dd>{q.data.header.custName ?? (q.data.header.custCode != null ? `#${q.data.header.custCode}` : '—')}</dd></div>
                <div><dt className="text-muted-foreground">Salesman</dt><dd>{q.data.header.salesman ?? '—'}</dd></div>
                <div><dt className="text-muted-foreground">Tender</dt><dd>{q.data.header.tender ?? '—'}</dd></div>
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Payment</CardTitle></CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                <div><dt className="text-muted-foreground">Cash</dt><dd>{fmtINR(q.data.payment.cashAmt)}</dd></div>
                <div><dt className="text-muted-foreground">Card</dt><dd>{fmtINR(q.data.payment.cardAmt)}</dd></div>
                {q.data.payment.cardLast4 && <div><dt className="text-muted-foreground">Card #</dt><dd className="font-mono">**** {q.data.payment.cardLast4}</dd></div>}
                {q.data.totals.refundAmt > 0 && <div><dt className="text-muted-foreground">Refund</dt><dd>{fmtINR(q.data.totals.refundAmt)}</dd></div>}
                {q.data.totals.alterAmt > 0 && <div><dt className="text-muted-foreground">Alter</dt><dd>{fmtINR(q.data.totals.alterAmt)}</dd></div>}
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Line items ({q.data.lines.length})</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <THead>
                  <TR>
                    <TH>Barcode</TH><TH>Item</TH><TH>Color</TH><TH>Size</TH>
                    <TH className="text-right">Qty</TH>
                    <TH className="text-right">MRP</TH>
                    <TH className="text-right">SP</TH>
                    <TH className="text-right">Discount</TH>
                    <TH className="text-right">Net</TH>
                  </TR>
                </THead>
                <TBody>
                  {q.data.lines.map((l) => (
                    <TR key={l.barcode}>
                      <TD className="font-mono">{l.barcode}</TD>
                      <TD>{l.itemName}</TD>
                      <TD>{l.color ?? '—'}</TD>
                      <TD>{l.size ?? '—'}</TD>
                      <TD className="text-right">{fmtNumber(l.qty)}</TD>
                      <TD className="text-right">{fmtINR(l.mrp)}</TD>
                      <TD className="text-right">{fmtINR(l.sp)}</TD>
                      <TD className="text-right">{fmtINR(l.fDscntAmt + l.mdAmt + l.dscntAmt)}</TD>
                      <TD className="text-right">{fmtINR(l.netAmt)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Totals</CardTitle></CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                <div><dt className="text-muted-foreground">Net</dt><dd>{fmtINR(q.data.totals.netAmt)}</dd></div>
                {q.data.totals.fDscntAmt > 0 && <div><dt className="text-muted-foreground">Bill discount</dt><dd>{fmtINR(q.data.totals.fDscntAmt)}</dd></div>}
                {q.data.totals.mdAmt > 0 && <div><dt className="text-muted-foreground">MD discount</dt><dd>{fmtINR(q.data.totals.mdAmt)}</dd></div>}
                {q.data.totals.dscntAmt > 0 && <div><dt className="text-muted-foreground">Item discount</dt><dd>{fmtINR(q.data.totals.dscntAmt)}</dd></div>}
                {q.data.totals.vat1 > 0 && <div><dt className="text-muted-foreground">VAT 1</dt><dd>{fmtINR(q.data.totals.vat1)}</dd></div>}
                {q.data.totals.vat2 > 0 && <div><dt className="text-muted-foreground">VAT 2</dt><dd>{fmtINR(q.data.totals.vat2)}</dd></div>}
                {q.data.totals.vat3 > 0 && <div><dt className="text-muted-foreground">VAT 3</dt><dd>{fmtINR(q.data.totals.vat3)}</dd></div>}
                <div className="col-span-2 sm:col-span-4"><dt className="text-muted-foreground">Bill total</dt><dd className="text-lg font-semibold">{fmtINR(q.data.totals.billAmt)}</dd></div>
              </dl>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
